import React from 'react';
import { scoreToColor } from './areaData.js';
import { scoreWord } from './scoreWords.js';
import './searchScore.css';

// The area score beside a search result (design S3 of the "Betyg i sökningen" canvas), in kartvy's search list and
// listvy's suggestions: the score's word in grey (scoreWords.js, the site's one list: "Eftertraktat 4,5") and the
// number in its score colour, no box. Only a result that is one area has a score: a single area, or an address or
// street (its area's). A kommun, län, postort or name covering several areas has none: every area is scored
// against its nearby areas, so one number for many of them would say little.

// { score } for a result covering exactly one area, or null.
export const searchResultScore = (regsoIds, areaStats) => {
    if (!Array.isArray(regsoIds) || regsoIds.length !== 1 || !areaStats) return null;
    const value = Number(areaStats[`combined_${String(regsoIds[0]).trim()}`]);
    if (!Number.isFinite(value)) return null;
    return { score: Math.round(Math.min(Math.max(value, 1), 6) * 2) / 2 };
};

// The score colour as text: its hue, darkened (on the light list) or lightened (on the dark one) just until it
// reads at 4.5:1 or better, since several score colours are too pale for text as they are.
const LIST_BG = { light: [255, 255, 255], dark: [34, 45, 43] }; // #ffffff, #222d2b (the lists' grounds)
const MIN_CONTRAST = 4.6;
const channel = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = ([r, g, b]) => (0.2126 * channel(r)) + (0.7152 * channel(g)) + (0.0722 * channel(b));
const contrast = (a, b) => {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
};
const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const rgbToHsl = ([r, g, b]) => {
    const [rn, gn, bn] = [r / 255, g / 255, b / 255];
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn) h = ((gn - bn) / d) + (gn < bn ? 6 : 0);
    else if (max === gn) h = ((bn - rn) / d) + 2;
    else h = ((rn - gn) / d) + 4;
    return [h / 6, s, l];
};
const hslToRgb = ([h, s, l]) => {
    if (!s) return [l, l, l].map((v) => Math.round(v * 255));
    const q = l < 0.5 ? l * (1 + s) : l + s - (l * s);
    const p = (2 * l) - q;
    const hue = (t) => {
        let x = t;
        if (x < 0) x += 1;
        if (x > 1) x -= 1;
        if (x < 1 / 6) return p + ((q - p) * 6 * x);
        if (x < 1 / 2) return q;
        if (x < 2 / 3) return p + ((q - p) * ((2 / 3) - x) * 6);
        return p;
    };
    return [hue(h + (1 / 3)), hue(h), hue(h - (1 / 3))].map((v) => Math.round(v * 255));
};
const toHex = (rgb) => `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
const inkCache = new Map();
export const scoreInk = (hex, theme) => {
    const key = `${hex}|${theme}`;
    if (inkCache.has(key)) return inkCache.get(key);
    const ground = LIST_BG[theme];
    let [h, s, l] = rgbToHsl(hexToRgb(hex));
    let rgb = hslToRgb([h, s, l]);
    for (let i = 0; i < 50 && contrast(rgb, ground) < MIN_CONTRAST; i += 1) {
        l = theme === 'light' ? Math.max(0, l - 0.02) : Math.min(1, l + 0.02);
        rgb = hslToRgb([h, s, l]);
    }
    const ink = toHex(rgb);
    inkCache.set(key, ink);
    return ink;
};

// The score cell: always rendered (empty without a score) so the rows' columns line up.
const SearchScore = ({ value, swedish = true }) => {
    if (!value) return <span className="search-score" aria-hidden="true" />;
    const colour = scoreToColor[value.score] || '#8f9aab';
    const label = swedish ? String(value.score).replace('.', ',') : String(value.score);
    const title = swedish ? `Betyg ${label}` : `Score ${label}`;
    return (
        <span
            className="search-score"
            title={title}
            style={{ '--score-ink-light': scoreInk(colour, 'light'), '--score-ink-dark': scoreInk(colour, 'dark') }}
        >
            <span className="search-score-label">{scoreWord(value.score, swedish)}</span>
            <span className="search-score-value">{label}</span>
        </span>
    );
};

export default SearchScore;
